import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router';
import moment from 'moment';
import {
	ScheduleWeekToolbar,
	ScheduleDayToolbar,
	ColumnHeading,
} from './comps/calendar';
import LoadingIndicator from 'components/loadingIndicator';
import ScheduleWeekView from './comps/scheduleWeekView';
import ScheduleDayView from './comps/scheduleDayView';
import { useApiService, useDataApiState } from 'hooks';
import { createCalendarScheduleService } from '../../services';
import { getNextWeekStartEndDate, getPreviousWeekStartEndDate, getWeekStartEndDate, getWeekStartEndDateForDate, getNextDay, getPreviousDay } from 'utilities/calendarFunctions';
import { WindowDetailsModal } from './comps/windowDetailsModal'
import { ScheduleManagementCalendarCriteria, ScheduleManagementCalendarResults } from 'types';
import CalendarScheduleFilters from './comps/calendarScheduleFilters';
import { useAppDispatch, useAppSelector } from '../../store';
import { getPracticeSitesForReferralSite } from '../../actions/appointmentActions';
import "./css/calendarScheduleView.css";
import AppointmentDetailsModal from './comps/appointmentDetailsModal'
import BlockDetailsModal from './comps/blockDetailsModal'

//interface ShowAddModalState {
//	show: boolean;
//	type: string | null;
//}

interface RecordIdRef {
	appointment: string;
	block: string;
	window: number;
}

// --- End Type Definitions ---

function getDefaultCriteria(): ScheduleManagementCalendarCriteria {
	return {
		calendarId: 0,
		calendarEntryCriteria: "",
		windowCriteria: "",
		dateCriteria: {
			minDate: '',
			maxDate: ''
		},
		appointmentTypeCriteria: ""
	};
}

export default function CalendarScheduleView() {
	const defaultCriteria = getDefaultCriteria();
	const weekStartEndDates = getWeekStartEndDate();
	const auth = useAppSelector((state) => state.auth);
	const [criteria, setCriteria] = useState<ScheduleManagementCalendarCriteria>({
		...defaultCriteria,
		calendarId: 0,
		dateCriteria: {
			minDate: weekStartEndDates.startDate,
			maxDate: weekStartEndDates.endDate
		}
	});
	const [calendarSchedule, setCalendarSchedule] = useDataApiState<ScheduleManagementCalendarResults>();
	const [showDayView, setShowDayView] = useState<boolean>(false);
	const [selectedDate, setSelectedDate] = useState<string | Date>(new Date());
	const [calendarId, setCalendarId] = useState<number>(0);
	const [isPracticeSitesLoaded, setIsPracticeSitesLoaded] = useState<boolean>(false);
	const [apptDetailsModalOpen, setApptDetailsModalOpen] = useState<boolean>(false);
	const [blockDetailsModalOpen, setBlockDetailsModalOpen] = useState<boolean>(false);
	const [availabilityModalOpen, setAvailabilityModalOpen] = useState<boolean>(false);
	/*const [showAddModal, setShowAddModal] = useState<ShowAddModalState>({ show: false, type: null });*/
	const recordId = useRef<RecordIdRef>({ appointment: "", block: "", window: 0 });

	const calendarScheduleService = useApiService(createCalendarScheduleService);

	const startingHour = '00:00';
	const endingHour = '23:00';

	const nextWeek = getNextWeekStartEndDate(criteria.dateCriteria.minDate, criteria.dateCriteria.maxDate);
	const previousWeek = getPreviousWeekStartEndDate(criteria.dateCriteria.minDate, criteria.dateCriteria.maxDate);
	const currentWeek = getWeekStartEndDate();
	const dispatch = useAppDispatch();

	const handleEntryClick = (record: any) => {
		const apptType = record.calendarEntryType;
		if (apptType === "Block" || apptType === "Shadow Block") {
			recordId.current.block = record.id;
			setBlockDetailsModalOpen(true)
		} else if (apptType === "MHD Appointment" || apptType === "Practice Scheduled Appointment") {
			recordId.current.appointment = record.id;
			setApptDetailsModalOpen(true);
		}
		else recordId.current.window = record.id;
	}

	const handleAvailabilityClick = (record: any) => {
		recordId.current.window = record.id;
		setAvailabilityModalOpen(true);
	};

	const handleWindowDetailsOnClose = () => {
		recordId.current.window = 0;
		setAvailabilityModalOpen(false);
	};

	const handleNextWeek = useCallback(() => {
		setCriteria({ ...criteria, dateCriteria: { minDate: nextWeek.startDate, maxDate: nextWeek.endDate } });
	}, [setCriteria, criteria, nextWeek.startDate, nextWeek.endDate]);

	const handlePreviousWeek = useCallback(() => {
		setCriteria({ ...criteria, dateCriteria: { minDate: previousWeek.startDate, maxDate: previousWeek.endDate } });
	}, [setCriteria, criteria, previousWeek.startDate, previousWeek.endDate]);

	const handleCurrentWeek = useCallback(() => {
		setSelectedDate(currentWeek.currentDate);
		setCriteria({ ...criteria, dateCriteria: { minDate: currentWeek.startDate, maxDate: currentWeek.endDate } });
	}, [setCriteria, criteria, currentWeek.startDate, currentWeek.endDate, currentWeek.currentDate]);

	const handleNextDay = useCallback((selectedDate: string) => {
		const nextDate = getNextDay(selectedDate);
		setSelectedDate(nextDate);
		const dates = getWeekStartEndDateForDate(nextDate);
		setCriteria({ ...criteria, dateCriteria: { minDate: dates.startDate, maxDate: dates.endDate } });
	}, [setCriteria, criteria]);

	const handlePreviousDay = useCallback((selectedDate: string) => {
		const previousDate = getPreviousDay(selectedDate);
		setSelectedDate(previousDate);
		const dates = getWeekStartEndDateForDate(previousDate);
		setCriteria({ ...criteria, dateCriteria: { minDate: dates.startDate, maxDate: dates.endDate } });
	}, [setCriteria, criteria]);

	const handleDateSelect = useCallback((selectedDate: string | Date) => {
		const instance = moment(selectedDate, 'YYYY-MM-DD');
		const validatedDate = instance.isValid() ? instance.format('yyyy-MM-DD') : moment().format('YYYY-MM-DD');
		setSelectedDate(validatedDate);
		const dates = getWeekStartEndDateForDate(validatedDate);
		setCriteria({ ...criteria, dateCriteria: { minDate: dates.startDate, maxDate: dates.endDate } });
	}, [setCriteria, criteria]);

	const handleShowDayView = useCallback((date: string | Date) => {
		setSelectedDate(date);
		setShowDayView(true);
	}, []);

	const handleShowDayView_Close = useCallback(() => {
		setShowDayView(false);
	}, []);

	const handleSearch = useCallback((selectedServiceSiteId: number) => {
		if (selectedServiceSiteId !== 0) {
			setCriteria({ ...criteria, calendarId: selectedServiceSiteId });
		}
	}, [criteria]);

	useEffect(() => {
		if (criteria.calendarId !== 0) {
			calendarScheduleService.getCalendarSchedule(criteria, setCalendarSchedule);
		}
	}, [criteria, setCalendarSchedule]);

	useEffect(() => {
		const today = moment();
		const startOfWeek = today.clone().startOf('week'); // Sunday as start

		const day = startOfWeek.clone();
		const calendarResult = {
			availability: [],
			entries: [],
			date: day.format('YYYY-MM-DD'),
			holiday: {
				id: 0,
				holidayDescription: '',
				holidayDate: day.format('YYYY-MM-DD'),
				isHoliday: false
			}
		};

		setCalendarSchedule({
			data: calendarResult,
			loading: false,
			error: null
		});

		const fetchInitialData = async () => {
			try {
				await dispatch(getPracticeSitesForReferralSite(auth.referralSiteId));
				setIsPracticeSitesLoaded(true);
			}
			catch (error: any) {
			}
		};
		fetchInitialData();
	}, []);

	const columnHeadings = Array.isArray(calendarSchedule.data)
		? calendarSchedule.data.map((day: any, index: number) => {
			// Defensive: ensure day.date exists and is valid
			const date = day?.date
				? moment(day.date, 'YYYY-MM-DD').format('YYYY-MM-DD')
				: '';
			return (
				<ColumnHeading
					key={date || index}
					date={date}
					showDayView={handleShowDayView}
				/>
			);
		})
		: null;
	return (
		<>
			{isPracticeSitesLoaded ? <>
				<CalendarScheduleFilters
					results={calendarSchedule}
					setCriteria={setCriteria}
					onCalendarScheduleSearch={handleSearch}
					setCalendarId={setCalendarId}
				/>
				{!showDayView &&
					<div className="calendar week-view">
						<ScheduleWeekToolbar
							selectedDate={selectedDate}
							startDate={moment(criteria?.dateCriteria?.minDate, 'MM/DD/YYYY').format("YYYY-MM-DD")}
							endDate={moment(criteria?.dateCriteria?.maxDate, 'MM/DD/YYYY').format("YYYY-MM-DD")}
							onNextWeek={handleNextWeek}
							onPreviousWeek={handlePreviousWeek}
							onTodayClick={handleCurrentWeek}
							handleDateSelect={handleDateSelect}
						/>
						<div className="calendar-days">
							<div className="spacer hour-marker-col">&nbsp;</div>
							<div className="days-cols">{columnHeadings}</div>
						</div>
					</div>
				}
				{showDayView &&
					<div className="calendar day-view">
						<ScheduleDayToolbar
							startDate={moment(selectedDate, 'YYYY-MM-DD').format("YYYY-MM-DD")}
							close={handleShowDayView_Close} onNextDay={handleNextDay}
							onPreviousDay={handlePreviousDay}
							onTodayClick={handleCurrentWeek}
							handleDateSelect={handleDateSelect}
						/>
						<div className="day-slots-header-container">
							<div className="day-slots-header">Windows</div>
							<div className="day-slots-header">Entries</div>
						</div>
					</div>
				}
				{
					calendarSchedule.loading ?
						<LoadingIndicator /> :
						<>
							{!showDayView &&
								<ScheduleWeekView
									hourHeight={240}
									startingHour={startingHour}
									endingHour={endingHour}
									dataSet={Array.isArray(calendarSchedule.data) ? calendarSchedule.data : []}
									showDayView={handleShowDayView}
									handleAvailabilityClick={handleAvailabilityClick}
									handleEntryClick={handleEntryClick}
								/>
							}
							{showDayView &&
								<ScheduleDayView
									hourHeight={240}
									startingHour={startingHour}
									endingHour={endingHour}
									dataSet={Array.isArray(calendarSchedule.data) ? calendarSchedule.data : []}
									selectedDate={typeof selectedDate === 'string' ? selectedDate : moment(selectedDate, 'YYYY-MM-DD').format('YYYY-MM-DD')}
									handleAvailabilityClick={handleAvailabilityClick}
									handleEntryClick={handleEntryClick}
								/>
							}
							<AppointmentDetailsModal
								show={apptDetailsModalOpen}
								onClose={() => setApptDetailsModalOpen(false)}
								refGuid={recordId.current.appointment}
							/>
							<BlockDetailsModal
								show={blockDetailsModalOpen}
								onClose={() => setBlockDetailsModalOpen(false)}
								refGuid={recordId.current.block}
							/>
							<WindowDetailsModal
								show={availabilityModalOpen}
								onClose={handleWindowDetailsOnClose}
								selectedId={recordId.current.window}
								calendarId={calendarId}
							/>
						</>
				}
			</> : <LoadingIndicator />}
			
		</>
	);
}